import { languageNames, type Language } from '@smarton-pbx/shared';
import { ChevronRight, ChevronsUpDown, CircleHelp, Ellipsis, Languages, LogOut, type LucideIcon, UserRound } from 'lucide-react';
import { type ComponentProps, Fragment, type ReactNode, useEffect, useState } from 'react';
import { Link, matchPath, NavLink, useLocation } from 'react-router';
import { language, showLanguage, t } from '../i18n';
import { guideLanguage, helpUrl, type HelpPages } from '../lib/help';
import { LogoMark } from './LogoMark';
import { Avatar, Menu } from './ui';

export interface NavLinkItem {
  to: string;
  label: string;
  /** In the phone's tab bar, where room is short. */
  short?: string;
  icon: LucideIcon;
  end?: boolean;
  /** E.g. an unread count, after the label. */
  badge?: ReactNode;
}

/** Pages under one heading that opens with a click; the group of the page shown is open. */
export interface NavGroupItem {
  /** Stable: the browser remembers whether it was left open. */
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavLinkItem[];
  /** On the heading while the group is closed (its pages' unread counts). */
  badge?: ReactNode;
}

export type NavItem = NavLinkItem | NavGroupItem;

const isGroup = (item: NavItem): item is NavGroupItem => 'items' in item;
const isCurrent = (item: NavLinkItem, pathname: string) => matchPath({ path: item.to, end: item.end ?? false }, pathname) !== null;

function savedOpen(id: string): boolean | null {
  try {
    const saved = localStorage.getItem(`nav-open:${id}`);
    return saved === null ? null : saved === '1';
  } catch {
    return null;
  }
}

function NavPage({ item }: { item: NavLinkItem }) {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} end={item.end ?? false}>
      <Icon size={18} aria-hidden />
      {item.label}
      {item.badge}
    </NavLink>
  );
}

/** A group in the sidebar: open as the person left it, and always while one of its pages is shown. */
function NavGroup({ group }: { group: NavGroupItem }) {
  const { pathname } = useLocation();
  const current = group.items.some((item) => isCurrent(item, pathname));
  const [open, setOpen] = useState(() => savedOpen(group.id) ?? current);
  useEffect(() => {
    if (current) setOpen(true);
  }, [current]);

  function toggle() {
    setOpen(!open);
    try {
      localStorage.setItem(`nav-open:${group.id}`, open ? '0' : '1');
    } catch {
      // Not kept (private window).
    }
  }

  const Icon = group.icon;
  return (
    <div className={`nav-group${open ? ' open' : ''}${current ? ' current' : ''}`}>
      <button type="button" className="nav-group-heading" aria-expanded={open} onClick={toggle} data-testid={`nav-group-${group.id}`}>
        <Icon size={18} aria-hidden />
        <span className="grow">{group.label}</span>
        {!open && group.badge}
        <ChevronRight size={15} className="nav-chevron" aria-hidden />
      </button>
      {open && (
        <div className="nav-group-items">
          {group.items.map((item) => (
            <NavPage key={item.to} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

/** The phone's tab bar: the first pages, and when there are more, a More menu with the rest by group. */
function TabBar({ nav }: { nav: NavItem[] }) {
  const { pathname } = useLocation();
  const pages = nav.flatMap((item) => (isGroup(item) ? item.items : [item]));
  const inBar = pages.length > 5 ? pages.slice(0, 4) : pages;
  const rest = nav
    .map((item) => (isGroup(item) ? { ...item, items: item.items.filter((page) => !inBar.includes(page)) } : item))
    .filter((item) => (isGroup(item) ? item.items.length > 0 : !inBar.includes(item)));
  const moreCurrent = !inBar.some((page) => isCurrent(page, pathname));
  const menuLink = (page: NavLinkItem, close: () => void) => {
    const Icon = page.icon;
    return (
      <NavLink key={page.to} to={page.to} end={page.end ?? false} role="menuitem" onClick={close}>
        <Icon size={16} aria-hidden /> {page.label}
        {page.badge}
      </NavLink>
    );
  };
  return (
    <nav className="tabbar" aria-label={t('Main')}>
      {inBar.map(({ to, label, short, icon: Icon, end, badge }) => (
        <NavLink key={to} to={to} end={end ?? false}>
          <Icon size={20} aria-hidden />
          {short ?? label}
          {badge}
        </NavLink>
      ))}
      {rest.length > 0 && (
        <Menu
          up
          label={t('More')}
          trigger={(props) => (
            <button type="button" className={`tabbar-more${moreCurrent ? ' active' : ''}`} {...props} data-testid="tabbar-more">
              <Ellipsis size={20} aria-hidden />
              {t('More')}
              {/* The unread counts of the pages in it. */}
              {rest.flatMap((item) => (isGroup(item) ? item.items : [item])).map((page) => page.badge && <Fragment key={page.to}>{page.badge}</Fragment>)}
            </button>
          )}
        >
          {(close) =>
            rest.map((item) =>
              isGroup(item) ? (
                <Fragment key={item.id}>
                  <div className="menu-heading">{item.label}</div>
                  {item.items.map((page) => menuLink(page, close))}
                </Fragment>
              ) : (
                menuLink(item, close)
              ),
            )
          }
        </Menu>
      )}
    </nav>
  );
}

/**
 * The signed-in layout: a sidebar on computers, a top bar and a tab bar on
 * phones. Used by tenant sites and the platform console.
 */
export function AppShell({
  brand,
  nav,
  sectionLabel,
  status,
  actions,
  help,
  user,
  accountPath,
  saveLanguage,
  onSignOut,
  children,
}: {
  brand: ReactNode;
  nav: NavItem[];
  sectionLabel?: string;
  status?: ReactNode;
  /** Shown next to the brand (e.g. the notification bell). */
  actions?: ReactNode;
  /** The user guide and which of its pages explains which screen. No guide: no Help button. */
  help?: { docsUrl: string | null; pages: HelpPages };
  /** photo: its address, if they have one. */
  user: { name: string; detail: string; photo?: string | null };
  accountPath: string;
  /** Keeps a language chosen in the menu for the person (else for this browser only). */
  saveLanguage?: (next: Language) => Promise<unknown>;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const otherLanguage: Language = language === 'lt' ? 'en' : 'lt';
  async function switchLanguage(close: () => void) {
    close();
    await saveLanguage?.(otherLanguage).catch(() => undefined);
    showLanguage(otherLanguage);
  }
  const userMenu = (up: boolean) => (
    <Menu
      up={up}
      label={t('Account')}
      trigger={(props) =>
        up ? (
          <button type="button" className="user-button" {...props} data-testid="user-menu">
            <span className="person">
              <Avatar name={user.name} photo={user.photo} />
              <span>
                <div className="name">{user.name}</div>
                <div className="detail">
                  {user.detail.split(' · ').map((part, i) => (
                    <Fragment key={i}>
                      {i > 0 && ' · '}
                      <span>{part}</span>
                    </Fragment>
                  ))}
                </div>
              </span>
            </span>
            <ChevronsUpDown size={16} className="subtle" aria-hidden />
          </button>
        ) : (
          <button type="button" className="ghost icon" {...props} aria-label={t('Account')} style={{ width: 38, height: 38 }}>
            <Avatar name={user.name} photo={user.photo} />
          </button>
        )
      }
    >
      {(close) => (
        <>
          <Link to={accountPath} onClick={close} role="menuitem">
            <UserRound size={16} aria-hidden /> {t('Your account')}
          </Link>
          {help?.docsUrl && (
            <HelpLink docsUrl={help.docsUrl} pages={help.pages} onClick={close} role="menuitem">
              <CircleHelp size={16} aria-hidden /> {t('Help')}
            </HelpLink>
          )}
          <button type="button" onClick={() => void switchLanguage(close)} role="menuitem" lang={otherLanguage} data-testid="switch-language">
            <Languages size={16} aria-hidden /> {languageNames[otherLanguage]}
          </button>
          <hr />
          <button type="button" className="danger-item" onClick={onSignOut} role="menuitem" data-testid="sign-out">
            <LogOut size={16} aria-hidden /> {t('Sign out')}
          </button>
        </>
      )}
    </Menu>
  );

  const helpButton = help?.docsUrl && (
    <HelpLink docsUrl={help.docsUrl} pages={help.pages} className="button ghost icon" aria-label={t('Help')} title={t('Help')}>
      <CircleHelp size={19} aria-hidden />
    </HelpLink>
  );

  return (
    <div className="app" data-testid="app-shell">
      <aside className="sidebar">
        <div className="sidebar-top">
          {brand}
          {helpButton}
          {actions}
        </div>
        <nav className="nav" aria-label={t('Main')}>
          {sectionLabel && <div className="nav-label">{sectionLabel}</div>}
          {nav.map((item) => (isGroup(item) ? <NavGroup key={item.id} group={item} /> : <NavPage key={item.to} item={item} />))}
        </nav>
        <div className="sidebar-footer">
          {status}
          {userMenu(true)}
        </div>
      </aside>

      <header className="mobile-top">
        {brand}
        <div className="row" style={{ gap: '0.25rem' }}>
          {helpButton}
          {actions}
          {userMenu(false)}
        </div>
      </header>

      <main className="page">{children}</main>

      <TabBar nav={nav} />
    </div>
  );
}

/** Opens the user guide, in a new tab, at the page about the screen you're on, in the browser's language where the guide has it. */
function HelpLink({
  docsUrl,
  pages,
  children,
  ...props
}: { docsUrl: string; pages: HelpPages; children: ReactNode } & Omit<ComponentProps<'a'>, 'href'>) {
  const { pathname } = useLocation();
  return (
    <a href={helpUrl(docsUrl, pages, pathname, guideLanguage())} target="_blank" rel="noopener" data-testid="help" {...props}>
      {children}
    </a>
  );
}

/** The tenant's logo, or a monogram and name. */
export function BrandMark({ name, logoUrl, to = '/' }: { name: string; logoUrl?: string | null; to?: string }) {
  return (
    <Link className="brand" to={to}>
      {logoUrl ? (
        <LogoMark name={name} logoUrl={logoUrl} />
      ) : (
        <>
          <span className="monogram" aria-hidden>
            {name.trim()[0]?.toUpperCase()}
          </span>
          {name}
        </>
      )}
    </Link>
  );
}
